import { ChangeDetectionStrategy, Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { AuthService } from '../../../core/services/auth.service';
import { SupportService } from '../support.service';

interface TopicOption {
  value: string;
  label: string;
}

const TOPICS_KUNDE: TopicOption[] = [
  { value: 'frage-anfrage-buchung', label: 'Frage zu meiner Anfrage / Buchung' },
  { value: 'zahlung-rechnung', label: 'Zahlung & Rechnung' },
  { value: 'mangel-streit', label: 'Mangel oder Streitfall' },
  { value: 'notfall', label: 'Notfall' },
  { value: 'konto-datenschutz', label: 'Konto & Datenschutz' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

const TOPICS_HANDWERKER: TopicOption[] = [
  { value: 'onboarding-verifizierung', label: 'Onboarding & Verifizierung' },
  { value: 'provision-auszahlung', label: 'Provision & Auszahlung' },
  { value: 'steuern-dac7', label: 'Steuern & DAC7' },
  { value: 'profil-problem', label: 'Profil-Problem' },
  { value: 'streit-mit-kunde', label: 'Streitfall mit Kunde' },
  { value: 'konto-datenschutz', label: 'Konto & Datenschutz' },
  { value: 'sonstiges', label: 'Sonstiges' },
];

const ALLOWED_MIMES = new Set(['image/jpeg', 'image/png', 'application/pdf']);
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Kontaktformular am Fuß der Hilfe-Seiten. Anker `#kontakt`.
 * Felder gemäß OnePager § 7. Vorbelegung wenn eingeloggt, Honeypot gegen Bots,
 * client-seitige Anhang-Validierung (Server prüft erneut).
 */
@Component({
  selector: 'app-kontaktformular',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './kontaktformular.component.html',
  styleUrl: './kontaktformular.component.scss',
})
export class KontaktformularComponent implements OnInit {
  @Input() audience: 'kunde' | 'handwerker' = 'kunde';
  @Input() bookingId: string | null = null;

  private readonly auth = inject(AuthService);
  private readonly support = inject(SupportService);

  readonly topics = computed(() =>
    this.audience === 'kunde' ? TOPICS_KUNDE : TOPICS_HANDWERKER,
  );

  // Form state
  topic = '';
  body = '';
  name = '';
  emailFrom = '';
  attachment: File | null = null;
  consent = false;
  honeypot = '';   // must remain empty

  // UI state
  readonly status = signal<'idle' | 'submitting' | 'success' | 'error'>('idle');
  readonly errorMessage = signal<string | null>(null);
  readonly attachmentError = signal<string | null>(null);
  readonly successTicket = signal<{ id: string; createdAt: string } | null>(null);

  ngOnInit(): void {
    const user = this.auth.currentUser;
    if (user) {
      this.name = [user.vorname, user.nachname].filter(Boolean).join(' ').trim() || '';
      this.emailFrom = user.email ?? '';
    }
  }

  onAttachmentSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.attachmentError.set(null);

    if (!file) {
      this.attachment = null;
      return;
    }
    if (file.size > MAX_BYTES) {
      this.attachmentError.set('Anhang zu groß (max 5 MB).');
      this.attachment = null;
      input.value = '';
      return;
    }
    if (!ALLOWED_MIMES.has(file.type)) {
      this.attachmentError.set('Nur JPG, PNG oder PDF erlaubt.');
      this.attachment = null;
      input.value = '';
      return;
    }
    this.attachment = file;
  }

  removeAttachment() {
    this.attachment = null;
    this.attachmentError.set(null);
  }

  bodyLengthHint(): string {
    const len = this.body.trim().length;
    if (len === 0) return 'Min 50 Zeichen';
    if (len < 50) return `${50 - len} Zeichen fehlen`;
    return `${len} / 2000`;
  }

  bodyValid(): boolean {
    const t = this.body.trim().length;
    return t >= 50 && t <= 2000;
  }

  async onSubmit(form: NgForm) {
    if (this.status() === 'submitting') return;
    this.errorMessage.set(null);

    if (!form.valid || !this.bodyValid() || !this.consent) {
      Object.values(form.controls).forEach((c) => c.markAsTouched());
      return;
    }

    this.status.set('submitting');
    try {
      const res = await this.support.createTicket({
        audience: this.audience,
        topic: this.topic,
        body: this.body.trim(),
        name: this.name.trim(),
        emailFrom: this.emailFrom.trim(),
        bookingId: this.bookingId,
        consentDataPrivacy: this.consent,
        honeypot: this.honeypot,
        attachment: this.attachment,
      });
      this.successTicket.set({ id: res.ticketId, createdAt: res.createdAt });
      this.status.set('success');
      // Reset for potential re-use:
      this.topic = '';
      this.body = '';
      this.attachment = null;
      this.consent = false;
      form.resetForm({
        name: this.name,
        emailFrom: this.emailFrom,
      });
    } catch (err: any) {
      this.status.set('error');
      const msg = err?.error?.error ?? err?.message ?? 'Beim Senden ist etwas schiefgelaufen.';
      this.errorMessage.set(msg);
    }
  }
}
